import { useCallback } from "react";
import { usePostHog } from "posthog-js/react";
// eslint-disable-next-line no-restricted-imports
import { Properties } from "posthog-js";
import { FEDERATION_TOKEN_COOKIE_KEY } from "@/config/cookiesKeys";
import { getCookie } from "@/utils/cookies";

export const PostHogEvents = {
  CancellationCompleted: "Subscription cancellation survey completed",
  PageView: "$pageview",
  NpsAsked: "Asked NPS feedback",
  NpsCompleted: "Completed NPS feedback",
  SwitchedColorScheme: "Switched color scheme",
  CustomerSatisfactionSurveyCompleted: "Customer satisfaction survey completed",
  CustomerSatisfactionSurveyDismissed: "Customer satisfaction survey dismissed",
  CustomerSatisfactionSurveyDisplayed: "Customer satisfaction survey displayed",
  DashboardAiBannerClicked: "Dashboard AI banner clicked",
  AiAgentsPageViewed: "AI agents page viewed",
  PromoBannerCtaClicked: "Promo banner CTA clicked",
  PromoBannerDismissed: "Promo banner dismissed",
  IntegrationsSnippetCopied: "Integrations snippet copied",
  IntegrationsCopyFailed: "Integrations copy failed",
  IntegrationsLinkClicked: "Integrations link clicked",
  IntegrationsTabSwitched: "Integrations tab switched",
  CustomizeAmountChanged: "Customize amount changed",
  CustomizeAmountLimitHit: "Customize amount limit hit",
  CustomizeContinueClicked: "Customize continue clicked",
  CustomizeSummaryOpened: "Customize summary opened",
  CustomizeSnackbarUndo: "Customize Snackbar Undo",
  ProxyStatsInteracted: "Proxy stats interacted",
  ProxyNetworkFeatureClicked: "Proxy network feature clicked",
  ProxyStatsLoadFailed: "Proxy stats load failed",
  CheckoutPaymentMethodSelected: "Checkout payment method selected",
  CheckoutContinueToStripeClicked: "Checkout continue to Stripe clicked",
};

const FederatedAccessTrackingValue = "utku@webshare.io";

/**
 * Whether staff are using this account through federated access. Read from
 * the cookie as each event is sent: `useFederatedUser` only learns it in an
 * effect, after a page's first events.
 */
function isFederated(): boolean {
  return Boolean(getCookie(FEDERATION_TOKEN_COOKIE_KEY)?.trim());
}

export function useEvents() {
  const posthog = usePostHog();

  const captureEvent = useCallback(
    (
      eventName: (typeof PostHogEvents)[keyof typeof PostHogEvents],
      payload?: Properties
    ) => {
      if (!posthog) return;

      if (isFederated()) {
        posthog.capture(eventName, {
          ...payload,
          federated_access: FederatedAccessTrackingValue,
        });
      } else {
        posthog.capture(eventName, payload);
      }
    },
    [posthog]
  );

  return { captureEvent };
}
